import { useCallback, useEffect, useState } from "react";
import { DollarSign, CheckCircle2, AlertTriangle, Receipt, Package, Users, TrendingUp, BarChart3, Coins, RotateCcw } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

const money = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money0 = (n) => `$${Math.round(Number(n || 0)).toLocaleString("en-CA")}`;
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const shortDay = (s) => new Date(s + "T00:00:00").toLocaleDateString("en-CA", { month: "short", day: "numeric" });

function defaultRange() {
  const end = new Date(); end.setHours(0, 0, 0, 0);
  const start = new Date(end); start.setDate(start.getDate() - 29);
  return { start: ymd(start), end: ymd(end) };
}

export default function Reports() {
  const [range, setRange] = useState(defaultRange());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/overview?start=${range.start}&end=${range.end}`);
      setData(data);
    } catch (err) { toast.error("Could not load report"); }
    finally { setLoading(false); }
  }, [range.start, range.end]);
  useEffect(() => { load(); }, [load]);

  const hasSales = data && data.invoice_count > 0 && data.total_sales > 0;

  return (
    <div className="h-full overflow-y-auto p-6 bg-white" data-testid="reports-page">
      <PageHeader title="Reports" subtitle="Sales analytics & performance">
        <div className="flex items-end gap-3">
          <label className="flex flex-col text-[11px] font-semibold uppercase tracking-wide text-zinc-400">From
            <input type="date" data-testid="report-from" value={range.start} max={range.end}
              onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
              className="mt-1 h-10 px-3 rounded-xl border border-zinc-200 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-900/10" />
          </label>
          <label className="flex flex-col text-[11px] font-semibold uppercase tracking-wide text-zinc-400">To
            <input type="date" data-testid="report-to" value={range.end} min={range.start}
              onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
              className="mt-1 h-10 px-3 rounded-xl border border-zinc-200 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-900/10" />
          </label>
        </div>
      </PageHeader>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi testid="kpi-total-sales" icon={<DollarSign size={18} />} tint="bg-zinc-900 text-white" label="Total Sales"
          value={loading ? "—" : money(data?.total_sales)} valueClass="text-zinc-900" />
        <Kpi testid="kpi-collected" icon={<CheckCircle2 size={18} />} tint="bg-emerald-100 text-emerald-700" label="Collected"
          value={loading ? "—" : money(data?.collected)} valueClass="text-emerald-600" />
        <Kpi testid="kpi-outstanding" icon={<AlertTriangle size={18} />} tint="bg-red-100 text-red-700" label="Outstanding"
          value={loading ? "—" : money(data?.outstanding)} valueClass="text-red-600" />
        <Kpi testid="kpi-hst" icon={<Receipt size={18} />} tint="bg-indigo-100 text-indigo-700" label="HST Collected"
          value={loading ? "—" : money(data?.hst_collected)} valueClass="text-zinc-900" />
      </div>

      {/* Secondary KPIs: Cost / Profit / Refunds (same source of truth as Dashboard) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Kpi testid="kpi-cost" icon={<Coins size={18} />} tint="bg-amber-100 text-amber-700" label="Cost (COGS)"
          value={loading ? "—" : money(data?.cost)} valueClass="text-zinc-900" />
        <Kpi testid="kpi-profit" icon={<TrendingUp size={18} />} tint="bg-emerald-100 text-emerald-700" label="Profit (Net − Cost)"
          value={loading ? "—" : money(data?.profit)} valueClass={(data?.profit ?? 0) < 0 ? "text-red-600" : "text-emerald-600"} />
        <Kpi testid="kpi-refunds" icon={<RotateCcw size={18} />} tint="bg-red-100 text-red-700" label="Refunds"
          value={loading ? "—" : money(data?.refunds)} valueClass="text-zinc-900" />
      </div>

      {/* Top products + Top customers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card title="Top Products by Revenue" icon={<Package size={16} className="text-zinc-400" />} testid="top-products">
          {!hasSales || (data.top_products || []).length === 0 ? (
            <Empty label="No sales data for the selected period." />
          ) : (
            <div className="space-y-3.5" data-testid="top-products-list">
              {data.top_products.map((p, i) => {
                const max = data.top_products[0].revenue || 1;
                const pct = Math.max(4, Math.round((p.revenue / max) * 100));
                return (
                  <div key={p.name} data-testid={`top-product-${i}`}>
                    <div className="flex items-baseline justify-between gap-3 mb-1">
                      <span className="text-sm font-medium text-zinc-700 truncate">{p.name}</span>
                      <span className="text-sm font-bold text-zinc-900 tabular-nums whitespace-nowrap">{money(p.revenue)}</span>
                    </div>
                    <div className="h-2.5 w-full rounded-full bg-zinc-100 overflow-hidden">
                      <div className="h-full rounded-full bg-zinc-900 transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card title="Top Customers" icon={<Users size={16} className="text-zinc-400" />} testid="top-customers">
          {!hasSales || (data.top_customers || []).length === 0 ? (
            <Empty label="No customer sales for the selected period." />
          ) : (
            <div className="divide-y divide-zinc-100" data-testid="top-customers-list">
              {data.top_customers.map((c, i) => (
                <div key={c.name} data-testid={`top-customer-${i}`} className="flex items-center gap-3 py-2.5">
                  <span className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-amber-100 text-amber-700" : "bg-zinc-100 text-zinc-500"}`}>{i + 1}</span>
                  <span className="text-sm font-medium text-zinc-700 truncate flex-1">{c.name}</span>
                  <span className="text-sm font-bold text-zinc-900 tabular-nums whitespace-nowrap">{money(c.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Sales revenue trend */}
      <Card title="Sales Revenue Trend" icon={<TrendingUp size={16} className="text-zinc-400" />} testid="sales-trend" className="mb-6">
        {!hasSales ? <Empty label="No sales data for the selected period." /> : (
          <div className="h-72 w-full" data-testid="sales-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.trend} margin={{ top: 10, right: 16, left: 4, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f3" vertical={false} />
                <XAxis dataKey="date" tickFormatter={shortDay} tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} minTickGap={24} />
                <YAxis tickFormatter={(v) => money0(v)} tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} width={70} />
                <Tooltip formatter={(v) => money(v)} labelFormatter={(l) => shortDay(l)} contentStyle={{ borderRadius: 12, border: "1px solid #e4e4e7", fontSize: 13 }} />
                <Line type="monotone" dataKey="sales" name="Sales" stroke="#18181b" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Collected vs Outstanding */}
      <Card title="Collected vs Outstanding" icon={<BarChart3 size={16} className="text-zinc-400" />} testid="collected-vs-outstanding">
        {!hasSales ? <Empty label="No sales data for the selected period." /> : (
          <div className="h-64 w-full" data-testid="collected-vs-outstanding-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[{ name: "Collected", value: data.collected, fill: "#10b981" }, { name: "Outstanding", value: data.outstanding, fill: "#ef4444" }]} margin={{ top: 10, right: 16, left: 4, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#71717a" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => money0(v)} tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} width={70} />
                <Tooltip formatter={(v) => money(v)} cursor={{ fill: "#fafafa" }} contentStyle={{ borderRadius: 12, border: "1px solid #e4e4e7", fontSize: 13 }} />
                <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={120}>
                  <Cell fill="#10b981" />
                  <Cell fill="#ef4444" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>
    </div>
  );
}

function Kpi({ icon, value, label, tint, valueClass = "", testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-zinc-200/80 bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">{label}</span>
        <span className={`h-9 w-9 rounded-xl flex items-center justify-center ${tint}`}>{icon}</span>
      </div>
      <p data-testid={`${testid}-value`} className={`font-heading font-extrabold text-2xl mt-3 tabular-nums ${valueClass}`}>{value}</p>
    </div>
  );
}

function Card({ title, icon, children, testid, className = "" }) {
  return (
    <div data-testid={testid} className={`rounded-2xl border border-zinc-200/80 bg-white p-5 shadow-sm ${className}`}>
      <div className="flex items-center gap-2 mb-4">{icon}<h2 className="font-heading text-base font-bold text-zinc-900">{title}</h2></div>
      {children}
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="py-12 flex flex-col items-center justify-center gap-2 text-zinc-400" data-testid="report-empty">
      <BarChart3 size={26} />
      <span className="text-sm">{label}</span>
    </div>
  );
}
